import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Loader2, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function AdminAuth() {
  const { admin, needsSetup, login, setup } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const email = "ukf.org.in@gmail.com";

  if (admin && admin !== null) return <Navigate to="/admin" replace />;

  const handleSetup = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await setup(password);
      toast.success("Admin account created");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Setup failed");
    } finally { setBusy(false); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally { setBusy(false); }
  };

  const field = "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm outline-none transition focus:border-ukf-red focus:ring-2 focus:ring-ukf-red/20";

  return (
    <div className="flex min-h-screen items-center justify-center bg-ukf-ink px-4">
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center text-center">
          <img src="/branding/ukf-emblem.png" alt="UKF" className="h-20 w-auto" />
          <h1 className="mt-4 font-serif text-2xl font-bold text-white">UKF Administration</h1>
          <p className="mt-1 text-sm text-slate-400">Union of Karate Federations</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-white p-7 shadow-xl">
          {needsSetup ? (
            <form onSubmit={handleSetup} data-testid="admin-setup-form">
              <div className="mb-5 flex items-center gap-2 text-ukf-red">
                <ShieldCheck className="h-5 w-5" />
                <h2 className="font-serif text-xl font-semibold text-ukf-ink">First-time setup</h2>
              </div>
              <p className="mb-5 text-sm text-slate-500">Create a secure password for the administrator account <strong className="text-slate-700">{email}</strong>.</p>
              <div className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700" htmlFor="s-pass">New password</label>
                  <input id="s-pass" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="setup-password-input" className={field} placeholder="At least 8 characters" />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700" htmlFor="s-confirm">Confirm password</label>
                  <input id="s-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="setup-confirm-input" className={field} />
                </div>
                <button type="submit" disabled={busy} data-testid="setup-submit-button"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-ukf-red px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-ukf-red-dark active:scale-[0.98] disabled:opacity-60">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Create account
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleLogin} data-testid="admin-login-form">
              <div className="mb-5 flex items-center gap-2 text-ukf-red">
                <Lock className="h-5 w-5" />
                <h2 className="font-serif text-xl font-semibold text-ukf-ink">Admin sign in</h2>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700" htmlFor="l-email">Email</label>
                  <input id="l-email" type="email" value={email} readOnly data-testid="login-email-input" className={`${field} bg-slate-50 text-slate-500`} />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700" htmlFor="l-pass">Password</label>
                  <input id="l-pass" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" className={field} autoFocus />
                </div>
                <button type="submit" disabled={busy} data-testid="admin-login-submit-button"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-ukf-red px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-ukf-red-dark active:scale-[0.98] disabled:opacity-60">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Sign in
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
